/**
 * Format a number with commas
 *
 * @param num Number to delimit with commas when over 999
 */
export function prettyFormatNumber(num: number) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * If `num < 0`, returns `less`, otherwise `more`. Zero intentionally returns
 * `more`.
 *
 * @param num Number to determine `more` or `less` than zero against
 * @param more Text to use as `more`
 * @param less Text to use as `less`
 */
export function moreOrLess(num: number, more = "more", less = "less") {
  return num <= 0 ? less : more;
}

/**
 * Return the percentage change value given the original value and a new value
 *
 * @param baseVal base value to compare against
 * @param updatedVal new value
 * @param precision Level of precision to round output to
 */
export function getPercentageChange(
  baseVal: number,
  updatedVal: number,
  precision = 2
) {
  // If no change, don't try to divide by zero
  if (baseVal === 0) return 0;

  if (baseVal === updatedVal) return 0;

  const delta = updatedVal - baseVal;
  return Number(((delta / baseVal) * 100).toFixed(precision));
}

/**
 * Count the number of times `0` appears in an array of numbers
 */
export function countZero(arr: number[]) {
  return arr.reduce((prev, curr) => (curr === 0 ? prev + 1 : prev), 0);
}

/**
 * Returns zero if the input value is null or not a number; Otherwise
 * simply returns the input value (this includes Infinity)
 *
 * @param num The input to check
 */
export function zeroIfNaN(num: any): number {
  const isNumber = typeof num === "number";
  if (isNumber) {
    return Number.isNaN(num) ? 0 : num;
  }
  return 0;
}

/**
 * Round any number to any level of precision
 * Defaults to 1 (integer)
 * @param value value to round
 * @param precision number of decimals to round to (defaults to nearest integer)
 */
export function round(value: number, precision = 0) {
  const multiplier = Math.pow(10, precision);
  return Math.round(value * multiplier) / multiplier;
}

/**
 * If datum is a number less than 10, keep single decimal
 * precision.
 * If datum is greater than 10, round to nearest integer.
 * @param hours number
 */
export function roundDatum(datum: number) {
  const precision = datum > 10 ? 0 : 1;
  return round(datum, precision);
}
